import React, { useState, useEffect } from 'react';
import { exercisesAPI } from '../../api';
import { Trophy, TrendingUp, Plus, X, ChevronRight } from 'lucide-react';

export function PersonalRecords({ userRole }) {
  const [prs, setPrs] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prsRes, exercisesRes] = await Promise.all([
        exercisesAPI.getMyPRs(),
        exercisesAPI.list()
      ]);
      setPrs(prsRes.data);
      setExercises(exercisesRes.data);
    } catch (error) {
      console.error('Error fetching PRs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group PRs by exercise
  const prsByExercise = prs.reduce((acc, pr) => {
    if (!acc[pr.exercise_id]) {
      acc[pr.exercise_id] = {
        exerciseName: pr.exercise_name,
        records: []
      };
    }
    acc[pr.exercise_id].records.push(pr);
    return acc;
  }, {});

  // Sort to get best PR for each exercise
  Object.values(prsByExercise).forEach(group => {
    group.records.sort((a, b) => b.value - a.value);
  });

  return (
    <div className="space-y-6" data-testid="personal-records">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personal Records</h1>
          <p className="text-gray-600">Track your PRs and celebrate progress</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          data-testid="add-pr-btn"
        >
          <Plus className="w-5 h-5" />
          Log PR
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-5 text-white">
          <Trophy className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{Object.keys(prsByExercise).length}</p>
          <p className="text-sm opacity-90">Exercises with PRs</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
          <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">{prs.length}</p>
          <p className="text-sm opacity-90">Total Records Logged</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
          <Trophy className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-2xl font-bold">
            {prs.filter(pr => pr.improvement && pr.improvement > 0).length}
          </p>
          <p className="text-sm opacity-90">Records Broken</p>
        </div>
      </div>

      {/* PR List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : Object.keys(prsByExercise).length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No PRs yet</h3>
          <p className="text-gray-500 mt-1">Start logging your personal records!</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="pr-list">
          {Object.entries(prsByExercise).map(([exerciseId, { exerciseName, records }]) => (
            <PRExerciseCard
              key={exerciseId}
              exerciseId={exerciseId}
              exerciseName={exerciseName}
              records={records}
              onSelect={() => setSelectedExercise({ id: exerciseId, name: exerciseName, records })}
            />
          ))}
        </div>
      )}

      {/* Add PR Form */}
      {showForm && (
        <PRForm
          exercises={exercises}
          onClose={() => setShowForm(false)}
          onSave={() => { setShowForm(false); fetchData(); }}
        />
      )}

      {/* PR History Modal */}
      {selectedExercise && (
        <PRHistoryModal
          exercise={selectedExercise}
          onClose={() => setSelectedExercise(null)}
        />
      )}
    </div>
  );
}

function PRExerciseCard({ exerciseId, exerciseName, records, onSelect }) {
  const bestPR = records[0];
  const recentPR = records.sort((a, b) => new Date(b.achieved_at) - new Date(a.achieved_at))[0];

  return (
    <div
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition cursor-pointer"
      onClick={onSelect}
      data-testid={`pr-card-${exerciseId}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">{exerciseName}</h3>
          <div className="flex items-center gap-4 mt-2">
            <div>
              <p className="text-sm text-gray-500">Best</p>
              <p className="text-xl font-bold text-indigo-600">
                {bestPR.value} <span className="text-sm font-normal text-gray-500">{bestPR.unit}</span>
              </p>
            </div>
            {recentPR.improvement && recentPR.improvement > 0 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                <TrendingUp className="w-4 h-4" />
                +{recentPR.improvement}%
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {records.length} record{records.length !== 1 ? 's' : ''} logged
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </div>
  );
}

function PRForm({ exercises, onClose, onSave }) {
  const [formData, setFormData] = useState({
    exercise_id: '',
    value: '',
    unit: 'kg',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.exercise_id || !formData.value) {
      setError('Please select an exercise and enter a value');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await exercisesAPI.createPR({
        ...formData,
        value: parseFloat(formData.value)
      });
      onSave();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save PR');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="pr-form-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Log Personal Record</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Exercise *</label>
              <select
                value={formData.exercise_id}
                onChange={(e) => setFormData(prev => ({ ...prev, exercise_id: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="pr-exercise-select"
              >
                <option value="">Select exercise...</option>
                {exercises.map(ex => (
                  <option key={ex.id} value={ex.id}>{ex.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="pr-value-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="kg">kg</option>
                  <option value="lbs">lbs</option>
                  <option value="reps">reps</option>
                  <option value="seconds">seconds</option>
                  <option value="minutes">minutes</option>
                  <option value="km">km</option>
                  <option value="miles">miles</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Any notes about this PR..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="save-pr-btn"
              >
                {saving ? 'Saving...' : 'Log PR'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function PRHistoryModal({ exercise, onClose }) {
  const sortedRecords = [...exercise.records].sort(
    (a, b) => new Date(b.achieved_at) - new Date(a.achieved_at)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{exercise.name}</h2>
              <p className="text-sm text-gray-500">PR History</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-3">
            {sortedRecords.map((record, idx) => (
              <div
                key={record.id}
                className={`p-3 rounded-lg ${idx === 0 ? 'bg-indigo-50 border border-indigo-200' : 'bg-gray-50'}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {record.value} {record.unit}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(record.achieved_at).toLocaleDateString()}
                    </p>
                  </div>
                  {record.improvement && record.improvement > 0 && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                      <TrendingUp className="w-3 h-3" />
                      +{record.improvement}%
                    </span>
                  )}
                </div>
                {record.notes && (
                  <p className="text-sm text-gray-600 mt-2">{record.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PersonalRecords;
